/** Client-side Twitch URL validation (mirrors backend/twitch_url_validation.py). */

export const INVALID_TWITCH_URL_MESSAGE =
  'Please paste a valid Twitch VOD, clip, or channel URL.';

const PLACEHOLDER_VOD_IDS = new Set(['1234567890']);
const PLACEHOLDER_CLIP_SLUGS_LOWER = new Set(['abcdefghij', 'abcdefghijklmnop']);
const PLACEHOLDER_CHANNELS = new Set(['username']);

const RESERVED_CHANNEL_NAMES = new Set([
  'videos',
  'video',
  'clip',
  'clips',
  'directory',
  'p',
  'settings',
  'subscriptions',
  'wallet',
  'broadcasts',
  'login',
  'signup',
  'jobs',
  'downloads',
  'creatorcamp',
  'turbo',
  'prime',
]);

const TWITCH_VOD_RE = /^https?:\/\/(?:www\.)?twitch\.tv\/videos\/(\d+)\/?$/i;
const TWITCH_CLIP_RE =
  /^https?:\/\/(?:www\.)?twitch\.tv\/([A-Za-z0-9_]{3,25})\/clip\/([A-Za-z0-9_-]{2,100})\/?$/i;
const CLIPS_TWITCH_RE = /^https?:\/\/clips\.twitch\.tv\/([A-Za-z0-9_-]{2,100})\/?$/i;
const TWITCH_CHANNEL_RE = /^https?:\/\/(?:www\.)?twitch\.tv\/([A-Za-z0-9_]{3,25})\/?$/i;

function normalizeTwitchUrl(url: string): string {
  const stripped = (url || '').trim();
  if (!stripped) {
    return '';
  }
  try {
    const parsed = new URL(stripped);
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return stripped;
    }
    let path = parsed.pathname;
    if (path.endsWith('/') && path.length > 1) {
      path = path.slice(0, -1);
    }
    return `${parsed.protocol}//${parsed.host.toLowerCase()}${path}`;
  } catch {
    return stripped;
  }
}

function hostAllowed(host: string): boolean {
  const normalized = host.toLowerCase().replace(/^www\./, '');
  return normalized === 'twitch.tv' || normalized === 'clips.twitch.tv';
}

function isPlaceholderVod(vodId: string): boolean {
  if (PLACEHOLDER_VOD_IDS.has(vodId)) {
    return true;
  }
  return vodId.toUpperCase() === 'YOUR_VOD_ID';
}

function isPlaceholderClip(slug: string): boolean {
  return PLACEHOLDER_CLIP_SLUGS_LOWER.has(slug.toLowerCase());
}

function isPlaceholderChannel(channel: string): boolean {
  return PLACEHOLDER_CHANNELS.has(channel.toLowerCase());
}

export function validateTwitchUrl(url: string): boolean {
  const normalized = normalizeTwitchUrl(url);
  if (!normalized) {
    return false;
  }

  let parsed: URL;
  try {
    parsed = new URL(normalized);
  } catch {
    return false;
  }

  if (!['http:', 'https:'].includes(parsed.protocol)) {
    return false;
  }
  if (!hostAllowed(parsed.host)) {
    return false;
  }

  const vodMatch = normalized.match(TWITCH_VOD_RE);
  if (vodMatch) {
    return !isPlaceholderVod(vodMatch[1]);
  }

  const clipMatch = normalized.match(TWITCH_CLIP_RE);
  if (clipMatch) {
    const channel = clipMatch[1];
    const slug = clipMatch[2];
    if (RESERVED_CHANNEL_NAMES.has(channel.toLowerCase())) {
      return false;
    }
    if (isPlaceholderChannel(channel) || isPlaceholderClip(slug)) {
      return false;
    }
    return true;
  }

  const clipsHostMatch = normalized.match(CLIPS_TWITCH_RE);
  if (clipsHostMatch) {
    return !isPlaceholderClip(clipsHostMatch[1]);
  }

  const channelMatch = normalized.match(TWITCH_CHANNEL_RE);
  if (channelMatch) {
    const channel = channelMatch[1];
    if (RESERVED_CHANNEL_NAMES.has(channel.toLowerCase())) {
      return false;
    }
    if (isPlaceholderChannel(channel)) {
      return false;
    }
    return true;
  }

  return false;
}
